import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

import { LayoutService } from '@layout/services';

import { LoaderComponent } from '@shared/components';
import { UserRole } from '@shared/models';
import { AuthService } from '@shared/services';

import VersionData from '../../../../assets/version.json';
import { AppScaleComponent } from '../app-scale/app-scale.component';
import { ProfileMenuComponent } from '../profile-menu/profile-menu.component';
import { SubscriptionCardComponent } from '../subscription-card/subscription-card.component';

import { AppMenuItemComponent } from './app-menu-item.component';
import { adminMenu } from './menus/admin-menu';
import { getCompanyMenu } from './menus/company-menu';
import { SideMenuItem } from './menus/menu-item';

@Component({
  selector: 'app-menu',
  templateUrl: './app-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    ButtonModule,
    TooltipModule,
    TranslateModule,
    LoaderComponent,
    AppScaleComponent,
    AppMenuItemComponent,
    ProfileMenuComponent,
    SubscriptionCardComponent,
  ],
})
export class AppMenuComponent {
  readonly appMenuComponentLoader = 'appMenuComponentLoader';
  readonly currentVersion = VersionData.version;

  private readonly user = toSignal(this.authService.user$, { initialValue: this.authService.user ?? null });
  private readonly companyId = computed(() => this.user()?.company?.id);
  readonly menuEntries = computed(() => {
    const menu = this.getMenu(!!this.companyId());
    // INFO (Denis) - the original index is kept so item keys stay stable when some items are hidden by role
    return menu.map((item, index) => ({ item, index, visible: !item.separator && this.canShow(item) }));
  });
  readonly showSubscriptionCard = computed(
    () =>
      !this.layoutService.isMenuCollapsed() &&
      !!this.user() &&
      this.authService.isUserInOneOfRoles([UserRole.Admin, UserRole.CompanyAdmin])
  );

  constructor(
    private readonly authService: AuthService,
    readonly layoutService: LayoutService
  ) {}

  private getMenu(hasCompany: boolean): SideMenuItem[] {
    if (!hasCompany && this.authService.isUserInRole(UserRole.Admin)) {
      return adminMenu;
    }

    return getCompanyMenu(this.authService);
  }

  private canShow(item: SideMenuItem): boolean {
    if (item.visible === false) {
      return false;
    }

    if (item.roles && !this.authService.isUserInOneOfRoles(item.roles)) {
      return false;
    }

    if (item.items?.length) {
      return item.items.some((subItem) => this.canShow(subItem));
    }

    return true;
  }
}
